/**
 * StudioTheme.jsx — Scoped light/dark theme just for the Studios admin
 * surface. Does NOT touch the founder-facing app. Implementation:
 *   - <StudioThemeProvider> wraps the Studios page and injects a `<style>`
 *     block that defines CSS variables.  Children use those vars via
 *     Tailwind utilities like `bg-[var(--s-bg)]`.
 *   - useStudioTheme() exposes { theme, setTheme, toggle }
 *   - <StudioThemeToggle /> is the actual sun/moon button drop-in.
 *
 * Persists user choice to localStorage('studio-theme') — defaults to
 * the OS-level prefers-color-scheme on first load.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Sun, Moon } from '@phosphor-icons/react';

const StudioThemeCtx = createContext({ theme: 'dark', setTheme: () => {}, toggle: () => {} });

const STORAGE_KEY = 'studio-theme';

const resolveInitial = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* localStorage may be blocked */ }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)')?.matches) {
    return 'light';
  }
  return 'dark';
};

const PALETTES = {
  dark: {
    '--s-bg': '#0A0A0A',
    '--s-surface': '#0A0A0A',
    '--s-surface-2': '#141414',
    '--s-surface-hover': '#1d1d1d',
    '--s-text': '#FFFFFF',
    '--s-text-muted': 'rgba(255,255,255,0.62)',
    '--s-text-dim': 'rgba(255,255,255,0.42)',
    '--s-text-faint': 'rgba(255,255,255,0.3)',
    '--s-border': 'rgba(255,255,255,0.10)',
    '--s-border-strong': 'rgba(255,255,255,0.30)',
    '--s-overlay': 'rgba(0,0,0,0.7)',
    '--s-grid': 'rgba(255,255,255,0.06)',
  },
  light: {
    '--s-bg': '#F7F7F8',
    '--s-surface': '#FFFFFF',
    '--s-surface-2': '#FFFFFF',
    '--s-surface-hover': '#F4F4F5',
    '--s-text': '#0A0A0A',
    '--s-text-muted': 'rgba(10,10,10,0.65)',
    '--s-text-dim': 'rgba(10,10,10,0.45)',
    '--s-text-faint': 'rgba(10,10,10,0.32)',
    '--s-border': 'rgba(10,10,10,0.10)',
    '--s-border-strong': 'rgba(10,10,10,0.25)',
    '--s-overlay': 'rgba(10,10,10,0.45)',
    '--s-grid': 'rgba(10,10,10,0.05)',
  },
};

export const StudioThemeProvider = ({ children, className = '' }) => {
  const [theme, setThemeState] = useState(resolveInitial);

  const setTheme = (t) => {
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
  };
  const toggle = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Inline CSS vars so a single wrapper covers nested portals / modals
  // (the variables cascade through React's `position: fixed` overlays).
  const styleVars = useMemo(() => PALETTES[theme], [theme]);

  // Force any modal portals (fixed positioned) to pick up the vars by also
  // setting them on :root when the provider is active.
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(styleVars).forEach(([k, v]) => root.style.setProperty(k, v));
    return () => {
      // Clear when unmounted so the founder app isn't polluted
      Object.keys(styleVars).forEach((k) => root.style.removeProperty(k));
    };
  }, [styleVars]);

  return (
    <StudioThemeCtx.Provider value={{ theme, setTheme, toggle }}>
      <div className={className} data-studio-theme={theme} style={styleVars}>
        {children}
      </div>
    </StudioThemeCtx.Provider>
  );
};

export const useStudioTheme = () => useContext(StudioThemeCtx);

export const StudioThemeToggle = ({ className = '' }) => {
  const { theme, toggle } = useStudioTheme();
  const isLight = theme === 'light';
  return (
    <button
      onClick={toggle}
      data-testid="studio-theme-toggle"
      aria-label={`Switch to ${isLight ? 'dark' : 'light'} mode`}
      title={`Switch to ${isLight ? 'dark' : 'light'} mode`}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-md border border-[var(--s-border)] hover:border-[var(--s-border-strong)] text-[var(--s-text-muted)] hover:text-[var(--s-text)] transition-colors ${className}`}
    >
      {isLight ? <Moon size={14} weight="fill" /> : <Sun size={14} weight="fill" />}
    </button>
  );
};

export default StudioThemeProvider;
